// frontend/src/components/AddDeviceModal.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';

import { useFarm } from '../context/FarmContext';


const RECOMMENDED_DEVICES = {
  small: 3,
  medium: 6,
  large: 20,
  enterprise: 50
};

export default function AddDeviceModal({ isOpen, onClose, targetPlot, plotSize = 'small', onDeviceProvisioned }) {
  const [quantity, setQuantity] = useState(3);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const { selectedFarmId, selectedPlotId } = useFarm();

  useEffect(() => {
    const recommended = RECOMMENDED_DEVICES[plotSize] || 3;
    setQuantity(recommended);
  }, [plotSize]);

  if (!isOpen) return null;

  const propertyId = selectedFarmId;
  const siteId = selectedPlotId;

  const handleProvision = async () => {
    setLoading(true);
    setError(null);

    try {
      // 1. Batch provision 'quantity' devices in PENDING state
      const provisionRes = await axios.post(
        `/api/devices/provision/${quantity}`,
        { propertyId, siteId, deviceType: 'ganoderma' },
        { withCredentials: true }
      );

      const createdDevices = provisionRes.data.devices;

      // 2. Trigger notification API for provisioning alert
      await axios.post(
        '/api/notifications/provision',
        {
          siteId,
          count: quantity,
          deviceIds: createdDevices.map((d) => d.id)
        },
        { withCredentials: true }
      );

      if (onDeviceProvisioned) {
        onDeviceProvisioned(createdDevices);
      }

      onClose();
    } catch (err) {
      console.error('Error provisioning devices:', err);
      setError(err.response?.data?.error || 'Failed to provision hardware devices.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(15, 23, 42, 0.65)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '16px'
    }}>
      <div style={{
        background: '#ffffff',
        borderRadius: '12px',
        maxWidth: '520px',
        width: '100%',
        padding: '28px',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
        position: 'relative'
      }}>
        <h3 style={{ margin: '0 0 8px 0', fontSize: '20px', color: '#0f172a' }}>
          Provision Physical Hardware
        </h3>
        <p style={{ margin: '0 0 20px 0', fontSize: '14px', color: '#64748b', lineHeight: 1.5 }}>
          Deploy hardware sensors to receive real-time hyper-local microclimate and soil telemetry for <strong>{targetPlot?.name || 'this plot'}</strong>.
        </p>

        {/* Mesh Network Card Explanation */}
        <div style={{
          background: '#f0f9ff',
          border: '1px solid #bae6fd',
          borderRadius: '8px',
          padding: '14px',
          marginBottom: '20px'
        }}>
          <h4 style={{ margin: '0 0 6px 0', fontSize: '13px', color: '#0369a1', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Connectivity & Mesh Network
          </h4>
          <p style={{ margin: 0, fontSize: '13px', color: '#0c4a6e', lineHeight: 1.4 }}>
            {quantity === 1 ? (
              <span><strong>Single Device:</strong> Connects directly via local Wi-Fi or cellular link.</span>
            ) : (
              <span><strong>Multi-Device Mesh ({quantity} units):</strong> Form a wireless relay network where 1 primary device acts as Gateway and remaining units relay telemetry automatically.</span>
            )}
          </p>
        </div>

        {error && (
          <div style={{ padding: '10px', background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', borderRadius: '6px', fontSize: '13px', marginBottom: '16px' }}>
            {error}
          </div>
        )}

        {/* Quantity Selector */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '8px' }}>
            Number of Devices to Provision:
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <input
              type="number"
              min="1"
              max="100"
              value={quantity}
              onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value, 10) || 1))}
              style={{
                width: '100px',
                padding: '10px 12px',
                borderRadius: '6px',
                border: '1px solid #cbd5e1',
                fontSize: '16px',
                fontWeight: 600
              }}
            />
            <span style={{ fontSize: '13px', color: '#64748b' }}>
              Recommended for {plotSize} plot: <strong>{RECOMMENDED_DEVICES[plotSize] || 3} units</strong>
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            style={{
              padding: '10px 18px',
              background: '#f1f5f9',
              color: '#475569',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 600
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleProvision}
            disabled={loading}
            style={{
              padding: '10px 20px',
              background: '#166534',
              color: '#ffffff',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 600
            }}
          >
            {loading ? 'Provisioning...' : `Provision ${quantity} Device(s)`}
          </button>
        </div>
      </div>
    </div>
  );
}
